import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLang } from '../i18n'
import { api, isApiError, type AgentId, type AgentStatusResponse, type AgentJob, type AgentChatResult, type AgentAction, type AgentProgressEvent, type WorkflowNode, type UserSkill, type ChatTurn, type ChatScope, type ChatImage, type ChatDeck, type SourceRef, type AgentLockAsk, type AgentLockChoice, type AgentScopeAsk, type AgentScopeChoice, type AgentUndoneTarget } from '../lib/api'
import { catalogNodeAsWorkflowNode, isSlashInvocable } from '../lib/workflow'
import { downloadBinary } from '../lib/download'
import {
  ACCEPTED_IMAGE_TYPES,
  MAX_CHAT_IMAGES,
  chatImageSrc,
  filesToChatImages,
  imageFilesFrom,
  type RejectedImage,
} from '../lib/chat-images'
import { DECK_ACCEPT, MAX_CHAT_DECKS, deckFilesFrom, isDeckFile, stageChatDeck } from '../lib/chat-decks'
import { refusalWords } from '../lib/slide-import'
import { IconAttach, IconHourglass } from './NavIcons'
import { FileIcon } from './FileIcons'
import { ClaudeLoginCta } from './ClaudeLoginCta'
import { CopilotLoginCta } from './CopilotLoginCta'
import { ChatUpdateRequiredCard } from './ChatUpdateRequiredCard'
import { SourcesModal } from './SourcesModal'
import { LockAskCard, UndoneList } from './LockAskCard'
import { ScopeAskCard } from './ScopeAskCard'
import {
  getModel,
  setModel as persistModel,
  getAgent,
  setAgent as persistAgent,
  getLocalSkillsEnabled,
  getDisabledLocalSkills,
  getClaudeExpiryDismissedUntil,
  setClaudeExpiryDismissedUntil,
  AUTO_MODEL,
  migrateClaudeModelToAuto,
} from '../lib/agent-prefs'
import { pickModel } from '../lib/model-default'
import type { PartKind } from '../lib/grid'
import { daysUntilLoginExpiry, EXPIRY_DISMISS_MS } from '../lib/claude-expiry'
import { beginAgentTask, useAgentBusy } from '../lib/agent-busy'
import { recordPrompt, finishPrompt, usePromptHistory } from '../lib/chat-prompt-history'
import { useChatDraft, getChatDraft, setChatDraftText, setChatDraftImages, setChatDraftDecks } from '../lib/chat-draft'
import { useChatQueue, setChatQueue, getChatQueue, type QueuedPrompt } from '../lib/chat-queue'
import { renderChatMarkdown } from '../lib/chat-markdown'
import { navigate, aiAgentSettingsPath } from '../lib/router'

const AGENTS = ['Claude Code', 'GitHub Copilot', 'OpenCode'] as const
const AGENT_LABEL: Record<(typeof AGENTS)[number], string> = {
  'Claude Code': '◆ Claude',
  'GitHub Copilot': '⊙ Copilot',
  OpenCode: '◇ OpenCode',
}
/** Maps this panel's display names to the ids GET /agent/status reports detection under. */
const AGENT_ID: Record<(typeof AGENTS)[number], AgentId> = {
  'Claude Code': 'claude',
  'GitHub Copilot': 'copilot',
  OpenCode: 'opencode',
}
/** An agent counts as usable once detected — and signed in, where Niobe can tell: a headless chat
 *  call against an installed-but-unauthenticated CLI fails every time, so treating "installed"
 *  alone as ready would let the member pick a guaranteed failure.
 *
 *  Claude needs a strict `true`. Copilot is unusable only on a strict `false`: its status read
 *  starts the Copilot runtime (~14 s cold), so the passerelle leaves it out until it has answered,
 *  and that unknown must never hide a Copilot that works. OpenCode has no auth check. */
function isAgentUsable(detail: AgentStatusResponse['agents'][AgentId] | undefined, id: AgentId): boolean {
  if (!detail) return false
  if (id === 'claude') return detail.authenticated === true
  if (id === 'copilot') return detail.authenticated !== false
  return true
}

/** Detected and definitely signed out — the agents with an assisted sign-in. Strict `false` for
 *  both: an absent value means the check has not answered, never "signed out". */
function needsSignIn(detail: AgentStatusResponse['agents'][AgentId] | undefined, id: AgentId): boolean {
  return (id === 'claude' || id === 'copilot') && !!detail && detail.authenticated === false
}
/** How often a running job is polled — fast enough for the streamed answer text (AB#381) to read
 *  as live and for the final answer to land within ~1 s of the run ending. Nothing is polled idle. */
const POLL_MS = 1000
/** Max conversational-memory budget sent to the LLM (chat turns only — never the document). */
const MAX_CTX_TOKENS = 20_000
/** Rough token estimate (≈ 4 chars/token) — enough to display + cap the chat context. */
const estTokens = (s: string): number => Math.ceil(s.length / 4)
const fmtTokens = (n: number): string => (n < 1000 ? `${n}` : `${(n / 1000).toFixed(1)}k`)
/** "heure de fin" shown per row in the past-requests dropdown — same shape as HistoryModal's
 *  own formatWhen (month/day + hour/minute, browser locale), for one consistent timestamp style
 *  across the app's two history surfaces. */
const formatEndedAt = (ts: number): string =>
  new Date(ts).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })

type ModelOpt = { id: string; name: string }

/**
 * Group a model list by provider for the picker. Copilot's catalogue legitimately mixes
 * Anthropic (Claude) and OpenAI (GPT) models, so we label the groups to make that clear
 * (the Claude entries under Copilot are real Copilot models, not the Claude Code agent's).
 */
function groupModels(models: ModelOpt[]): { label: string; items: ModelOpt[] }[] {
  const order = ['Auto', 'Anthropic (Claude)', 'OpenAI (GPT)', 'Other']
  const groups: Record<string, ModelOpt[]> = {}
  for (const m of models) {
    const k =
      m.id === 'auto'
        ? 'Auto'
        : /claude|sonnet|opus|haiku/i.test(m.id)
          ? 'Anthropic (Claude)'
          : /gpt|^o\d/i.test(m.id)
            ? 'OpenAI (GPT)'
            : 'Other'
    ;(groups[k] ??= []).push(m)
  }
  return order.filter((k) => groups[k]?.length).map((k) => ({ label: k, items: groups[k]! }))
}

type ItemKind = 'chat' | 'note'

/**
 * One chat line. Agentic lines are backed by a passerelle job (`jobId`) so they survive a
 * page refresh and are re-attached on mount; `note` lines are local guidance with no job.
 */
interface ChatItem {
  id: string
  jobId?: string
  kind: ItemKind
  q: string
  status: 'running' | 'done' | 'error' | 'cancelled'
  message?: string
  actions?: AgentAction[]
  /** KB documents this answer consulted, one entry per file. Absent when no KB search ran (and
   *  for the OpenCode agent, whose tools run out of process — see DEBT.md), which is what hides
   *  the "Sources" button rather than showing "0 sources". */
  sources?: SourceRef[]
  tokensIn?: number
  tokensOut?: number
  durationMs?: number
  /** The LLM that produced this answer (e.g. "opus", "claude-sonnet-4.6", "gpt-5.4"). */
  model?: string
  /** AB#401: the run was routed to a lighter model — the answer offers "Redo with Opus". */
  routed?: boolean
  /** The run's usage record id — the `redoOf` of its redo. */
  usageId?: string
  /** The local Claude session this answer ran in (AB#380) — the thread's next message resumes it. */
  sessionId?: string
  note?: string
  /** Live tool-call steps received while status === 'running'. */
  progress?: AgentProgressEvent[]
  /** The answer text written so far, while status === 'running' (AB#381). */
  partialText?: string
  /** The run is paused on a question about targets that stayed locked (lock conflicts, AB#303),
   *  or about widening the chat target (`kind: 'scope'`, AB#337). */
  ask?: AgentLockAsk | AgentScopeAsk
  /** What the finished run left undone because of a lock, and why. */
  undone?: AgentUndoneTarget[]
  /** Images sent with this question, echoed as thumbnails on its bubble. Client-side only: the
   *  passerelle keeps the prompt, not the bytes, so a refresh recovers the turn without them. */
  images?: ChatImage[]
  /** PowerPoint decks sent with this question (staged, AB#452), named on its bubble. Client-side only. */
  decks?: ChatDeck[]
  /** This turn failed because the member's local Claude Code login is dead (the job carried
   *  `errorCode: 'agent_auth_required'`). Rendered as a sign-in prompt rather than a red error —
   *  and, since it is read back off the job, it survives a refresh. */
  authRequired?: boolean
  /** This turn failed because the API refused the model: the local Claude Code is too old for it
   *  (`errorCode: 'agent_update_required'`, AB#370). Rendered as an update card, never a red error;
   *  the versions come off the job's own fields, so it survives a refresh too. */
  updateRequired?: { requiredVersion: string; currentVersion?: string }
}

/** Build a chat line from a job's current state (used on completion and on refresh recovery). */
function buildItem(job: AgentJob): ChatItem {
  const base: ChatItem = { id: job.id, jobId: job.id, kind: 'chat', q: job.prompt ?? '', status: job.status }
  if (job.status === 'running') {
    return {
      ...base,
      ...(job.progress?.length ? { progress: job.progress } : {}),
      ...(job.partialText ? { partialText: job.partialText } : {}),
      ...(job.ask ? { ask: job.ask } : {}),
    }
  }
  if (job.status === 'error') {
    return {
      ...base,
      status: 'error',
      note: job.error ?? 'The local agent failed.',
      // Compared as a literal on purpose: lib/api is mocked wholesale in the component tests, so a
      // runtime constant imported from it would throw "No export is defined on the mock".
      ...(job.errorCode === 'agent_auth_required' ? { authRequired: true } : {}),
      ...(job.errorCode === 'agent_update_required' && job.requiredVersion
        ? { updateRequired: { requiredVersion: job.requiredVersion, ...(job.currentVersion ? { currentVersion: job.currentVersion } : {}) } }
        : {}),
    }
  }
  if (job.status === 'cancelled') return base
  const r = (job.result ?? {}) as AgentChatResult
  return {
    ...base,
    message: r.message ?? '',
    actions: r.actions ?? [],
    // Read back from the persisted job result, so the button survives a refresh.
    ...(r.sources?.length ? { sources: r.sources } : {}),
    ...(r.undone?.length ? { undone: r.undone } : {}),
    tokensIn: r.tokensIn ?? 0,
    tokensOut: r.tokensOut ?? 0,
    durationMs: r.durationMs ?? 0,
    ...(r.model ? { model: r.model } : {}),
    ...(r.routed ? { routed: true } : {}),
    ...(r.usageId ? { usageId: r.usageId } : {}),
    ...(r.sessionId ? { sessionId: r.sessionId } : {}),
  }
}

/**
 * The thread's local Claude session (AB#380): the one the latest answered turn ran in. Absent when
 * that turn came from another agent or a skill run — the next message then starts a new session
 * from the flattened history. Lives with `items`, so "forget the conversation" and a bid switch
 * end it too.
 */
function threadSessionId(items: ChatItem[]): string | undefined {
  for (let i = items.length - 1; i >= 0; i--) {
    const it = items[i]!
    if (it.kind === 'chat' && it.status === 'done' && it.message) return it.sessionId
  }
  return undefined
}

/**
 * Build the conversational memory from completed chat turns, most-recent-first up to the
 * token budget (oldest turns are dropped when over MAX_CTX_TOKENS). Only the chat questions
 * and answers are carried — never the document text. Returns the turns to send (chronological),
 * the token total, and whether anything was trimmed.
 */
function buildHistory(items: ChatItem[]): { turns: ChatTurn[]; tokens: number; trimmed: boolean } {
  const done = items.filter((it) => it.kind === 'chat' && it.status === 'done' && it.message)
  const turns: ChatTurn[] = []
  let tokens = 0
  let trimmed = false
  for (let i = done.length - 1; i >= 0; i--) {
    const it = done[i]!
    const cost = estTokens(it.q) + estTokens(it.message ?? '')
    if (tokens + cost > MAX_CTX_TOKENS && turns.length > 0) {
      trimmed = true
      break
    }
    turns.unshift({ role: 'user', content: it.q }, { role: 'assistant', content: it.message ?? '' })
    tokens += cost
  }
  return { turns, tokens, trimmed }
}

/** Animated "the AI is working" indicator. */
function Thinking({ label }: { label: string }) {
  return (
    <span className="thinking" role="status" aria-live="polite">
      <span className="dots" aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
      {label}
    </span>
  )
}

/** How many follow-up prompts may wait while a chat job runs. */
const MAX_QUEUED = 2

/** Extract /v1/export-files/ download paths from an agent message. */
const EXPORT_URL_RE = /(\/v1\/export-files\/[^\s"'`<>()[\]]+)/g

function extractDownloadUrls(message: string): { url: string; filename: string }[] {
  const matches = [...message.matchAll(EXPORT_URL_RE)]
  return matches.map((m) => {
    const url = m[1]!
    const filename = decodeURIComponent(url.split('/').pop() ?? 'export')
    return { url, filename }
  })
}

export function ChatPanel({
  bid,
  room,
  partKind,
  onCollapse,
}: {
  bid: string | null
  room: string | null
  /** The open part's kind — lets the passerelle route a mechanical edit to a lighter model. */
  partKind?: PartKind
  onCollapse?: () => void
}) {
  const { t } = useLang()
  const qc = useQueryClient()
  const savedAgent = getAgent()
  const [agent, setAgent] = useState<(typeof AGENTS)[number]>(
    savedAgent && (AGENTS as readonly string[]).includes(savedAgent)
      ? (savedAgent as (typeof AGENTS)[number])
      : 'Claude Code',
  )
  // Agent-picker dropdown on the chat logo (◆): which local agent powers this chat.
  const [agentMenuOpen, setAgentMenuOpen] = useState(false)
  // Whether ANY local agent CLI was found on this machine at all — distinct from `agentAvailable`
  // one level up (App.tsx), which only means "this is Niobe Desktop" and says nothing about
  // whether a CLI is actually installed. `undefined` while the first check is still in flight, so
  // the empty state below only renders once we have a definite answer (never flashes on the way
  // to "yes, detected" — the common case).
  const agentStatusQuery = useQuery({ queryKey: ['agent-status'], queryFn: () => api.agentStatus(), staleTime: 10_000 })
  const noAgentDetected = agentStatusQuery.data !== undefined && !agentStatusQuery.data.available
  // Distinct from noAgentDetected: at least one CLI is present, but none of them are actually
  // usable right now — the ones installed are all signed out (Claude, or a Copilot whose status
  // read said so; OpenCode has no auth check here and counts as usable once detected). Same "wait
  // for a definite answer" care: only true once the query has resolved.
  const agentStatusData = agentStatusQuery.data
  const noUsableAgent =
    agentStatusData !== undefined &&
    agentStatusData.available &&
    (Object.keys(agentStatusData.agents) as AgentId[]).every((id) => !isAgentUsable(agentStatusData.agents[id], id))
  /**
   * Which agent's sign-in the next message is waiting on, if any: the agent this chat is pointed
   * at when it is detected and definitely signed out — or, when nothing on this machine is usable
   * anyway (a persisted choice that is no longer detected), the first signed-out one. When set,
   * the panel shows the sign-in banner for that agent and refuses to send — but keeps the
   * transcript, unlike the full-panel swap this replaced, which hid the conversation and offered
   * only a link to Settings.
   */
  const signInAgent: 'claude' | 'copilot' | null = (() => {
    const selected = AGENT_ID[agent]
    if ((selected === 'claude' || selected === 'copilot') && needsSignIn(agentStatusData?.agents[selected], selected)) return selected
    if (!noUsableAgent) return null
    if (needsSignIn(agentStatusData?.agents.claude, 'claude')) return 'claude'
    if (needsSignIn(agentStatusData?.agents.copilot, 'copilot')) return 'copilot'
    return null
  })()
  const signInBlocking = signInAgent !== null
  /**
   * The proactive expiry warning: whole days until the local Claude login's HARD expiry, or null.
   *
   * Null covers "further off than three days", "unknown" (an older passerelle, the macOS Keychain),
   * and — deliberately — whenever the sign-in banner is up: a member being told to sign in does not
   * also need to be told their old login is about to expire, and the banner already leads to the
   * one action that fixes both.
   */
  const [expiryDismissedUntil, setExpiryDismissedUntil] = useState(() => getClaudeExpiryDismissedUntil())
  /**
   * The opt-in long-lived token (#55), when it is what authenticates right now.
   *
   * It silences the expiry strip: the member's `/login` credential stays on disk and keeps
   * expiring, but nothing is using it, so "Your Claude sign-in expires in N days" with a Renew
   * button would point at a fix that cannot change anything. The passerelle already omits
   * `loginExpiresAt` in this mode; checking here too means a newer web against an older passerelle
   * cannot regress it, and the warning comes straight back the moment the token is removed or
   * rejected (`tokenMode` flips to 'login' in both cases).
   */
  const longLivedToken = agentStatusData?.agents.claude?.tokenMode === 'long-lived'
  /** Niobe stopped injecting a stored token after it was refused — the member has a dead one to
   *  clear in Settings. Never a reason to hide the sign-in below: it is a valid fix again. */
  const tokenRejected = agentStatusData?.agents.claude?.tokenRejected === true
  const expiresInDays =
    signInBlocking || longLivedToken ? null : daysUntilLoginExpiry(agentStatusData?.agents.claude?.loginExpiresAt)
  const showExpiryStrip = expiresInDays !== null && Date.now() >= expiryDismissedUntil
  const dismissExpiry = (): void => {
    const until = Date.now() + EXPIRY_DISMISS_MS
    setClaudeExpiryDismissedUntil(until)
    setExpiryDismissedUntil(until)
  }
  const [models, setModels] = useState<{ id: string; name: string }[]>([])
  /** The picker's value: a catalogue model id, or AUTO_MODEL (Claude Code only) — the one case
   *  where no model is sent and the passerelle may route a run to a lighter model (AB#401/411). */
  const [model, setModel] = useState<string>('')
  const [modelsLoading, setModelsLoading] = useState(false)
  // The unsent entry (this text, and the images below) lives in lib/chat-draft.ts, keyed by bid:
  // it outlives this panel, which App.tsx unmounts on every view but the editor and dashboard,
  // and never follows the member into another bid. Each setter writes to the bid of the render
  // it came from.
  const unsent = useChatDraft(bid)
  const draft = unsent.text
  const setDraft = (text: string): void => setChatDraftText(bid, text)
  const [items, setItems] = useState<ChatItem[]>([])
  // The answer whose "Sources consulted" dialog is open, or null. Holding the list rather than an
  // item id keeps the dialog stable while `items` is re-polled underneath it.
  const [openSources, setOpenSources] = useState<SourceRef[] | null>(null)
  // Target scope: 'part' = the open part, 'artefact' = its whole artefact, 'bid' = all artefacts.
  // Resets to 'part' (or 'bid' when no part is open) whenever a new part is opened (room changes).
  const [scope, setScope] = useState<ChatScope>(room ? 'part' : 'bid')
  const [scopeMenuOpen, setScopeMenuOpen] = useState(false)
  // Per-message retrieval-tool toggles: whether the agent may search this bid's own knowledge
  // base and/or the company-wide one on the NEXT message. Plain in-memory state — deliberately
  // not persisted (unlike the sticky agent/model choice in lib/agent-prefs.ts) and independent
  // of the open part, so no reset-on-room-change effect here.
  const [bidKb, setBidKb] = useState(true)
  // Corp KB is tri-state: null = the member has not chosen, so plain chat treats it as off while a
  // slash-skill lets its own default apply (the `references` skill searches the corporate KB).
  const [corporateKb, setCorporateKb] = useState<boolean | null>(null)
  // Sticky per-member preference — toggled from Settings → AI agent → Claude (LocalSkillsSettings)
  // now, not from here; read once at mount to shape this chat's tool exposure.
  const [localSkillsEnabled] = useState(getLocalSkillsEnabled())
  // Per-skill opt-out layered under the master toggle above — same story, read once here.
  const [disabledSkills] = useState<string[]>(getDisabledLocalSkills())
  // ── Image attachments (paste / drop / pick) ─────────────────────────────
  // Per-message and in-memory only: the bytes ride with the next send and are never uploaded to
  // the backend (see lib/chat-images.ts). Kept as data-URL-able base64 so a thumbnail needs no
  // object URL to revoke. Stored with the unsent text above, so they survive navigation too.
  const attachments = unsent.images
  const setAttachments = (update: ChatImage[] | ((prev: ChatImage[]) => ChatImage[])): void =>
    setChatDraftImages(bid, update)
  const [attachError, setAttachError] = useState<string | null>(null)
  const [dropping, setDropping] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // ── Deck attachments (AB#452) ───────────────────────────────────────────
  // A .pptx is staged on the backend as soon as it is attached (lib/chat-decks.ts) — the agent
  // imports it with import_pptx — and the message carries only its { stagedId, name }. Kept with
  // the unsent entry, like the images. `stagingDecks` are the ones still uploading.
  const attachedDecks = unsent.decks ?? []
  const setAttachedDecks = (update: ChatDeck[] | ((prev: ChatDeck[]) => ChatDeck[])): void =>
    setChatDraftDecks(bid, update)
  const [stagingDecks, setStagingDecks] = useState<Array<{ key: string; name: string }>>([])

  /** Message for the first refused file — named, so the member knows which one and why. */
  function rejectionMessage(rejected: RejectedImage[]): string | null {
    const first = rejected[0]
    if (!first) return null
    const key = first.reason === 'type' ? 'chat_img_err_type' : first.reason === 'size' ? 'chat_img_err_size' : 'chat_img_err_count'
    return t(key).replace('{name}', first.name).replace('{n}', String(MAX_CHAT_IMAGES))
  }

  /** Why a deck could not be staged, in the import dialog's words when the backend named the refusal. */
  function deckRefusal(e: unknown, name: string): string {
    const words = refusalWords(e, 'stage')
    const reason = words
      ? Object.entries(words.vars ?? {}).reduce((acc, [k, v]) => acc.replaceAll(`{${k}}`, () => String(v)), t(words.key))
      : isApiError(e) && e.serverMessage
        ? e.serverMessage
        : t('prompt_failed')
    return t('chat_deck_err').replace('{name}', name).replace('{reason}', reason)
  }

  /** Stage one deck for the agent; it joins the attachments once the backend has vetted it. */
  async function attachDeck(file: File): Promise<void> {
    const name = file.name || 'deck.pptx'
    const key = crypto.randomUUID()
    setStagingDecks((s) => [...s, { key, name }])
    try {
      const deck = await stageChatDeck(file)
      setAttachedDecks((prev) => [...prev, deck].slice(0, MAX_CHAT_DECKS))
    } catch (e) {
      setAttachError(deckRefusal(e, name))
    } finally {
      setStagingDecks((s) => s.filter((d) => d.key !== key))
    }
  }

  /** Add files coming from a paste, a drop or the file picker: decks are staged, images read here. */
  async function attachFiles(all: File[]): Promise<void> {
    if (all.length === 0) return
    // The deck count reads the store, so a drop right after another one does not see a stale count.
    let deckSlots = MAX_CHAT_DECKS - (getChatDraft(bid).decks?.length ?? 0) - stagingDecks.length
    for (const deck of all.filter(isDeckFile)) {
      if (deckSlots <= 0) {
        setAttachError(t('chat_deck_err_count').replace('{name}', deck.name).replace('{n}', String(MAX_CHAT_DECKS)))
        continue
      }
      deckSlots -= 1
      void attachDeck(deck)
    }
    const files = all.filter((f) => !isDeckFile(f))
    if (files.length === 0) return
    const { images, rejected } = await filesToChatImages(files, attachments.length)
    // Re-apply the cap on the way in: two pastes in the same tick would both read the same
    // pre-update length above, and the passerelle refuses an over-long list outright.
    if (images.length) setAttachments((prev) => [...prev, ...images].slice(0, MAX_CHAT_IMAGES))
    setAttachError(rejectionMessage(rejected))
  }

  const inputRef = useRef<HTMLTextAreaElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const agentMenuRef = useRef<HTMLDivElement>(null)
  const scopeMenuRef = useRef<HTMLDivElement>(null)
  const historyMenuRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)

  // ── Slash-command skill menu ───────────────────────────────────────────

  const [skillMenuIdx, setSkillMenuIdx] = useState(0)

  // ── Past-requests recall (in-memory, in-tab only — see lib/chat-prompt-history.ts) ─────
  // Independent of `items`: survives clearChat() and bid switches, unlike the visible transcript.
  const promptHistory = usePromptHistory()
  const [historyMenuOpen, setHistoryMenuOpen] = useState(false)
  const [historyFilter, setHistoryFilter] = useState('')
  // Index into displayHistory (see below). Kept as a sentinel meaning "the most recent one" so it
  // doesn't need recomputing by hand every time the filter narrows the list — see activeHistoryIdx.
  const [historyMenuIdx, setHistoryMenuIdx] = useState(Number.MAX_SAFE_INTEGER)
  const historyListRef = useRef<HTMLDivElement>(null)
  // -1 = not currently recalling; else an index into promptHistory (0 = most recent) driving
  // the shell-style ArrowUp/ArrowDown recall in the empty composer.
  const [recallIdx, setRecallIdx] = useState(-1)
  // Full-text filter over the past requests (case-insensitive substring match).
  const filteredHistory = historyFilter.trim()
    ? promptHistory.filter((p) => p.q.toLowerCase().includes(historyFilter.trim().toLowerCase()))
    : promptHistory
  // The dropdown shows OLDEST at the top, MOST RECENT at the bottom (by the search box, like a
  // normal chat log) — the opposite order from promptHistory/filteredHistory, which stay
  // most-recent-first since that's what the shell-style recall above needs.
  const displayHistory = [...filteredHistory].reverse()
  // Clamp the sentinel/stale index against the CURRENT (possibly just-filtered) list so it always
  // resolves to a real row — defaulting to the last one, i.e. the most recent match.
  const activeHistoryIdx = Math.min(historyMenuIdx, Math.max(displayHistory.length - 1, 0))

  const workflowQuery = useQuery({
    queryKey: ['workflow', bid],
    queryFn: () => api.workflow(bid as string),
    enabled: Boolean(bid),
    staleTime: 30_000,
  })
  // `api.workflow` resolves to `null` (not undefined) once the "no workflow yet" response
  // has actually come back — as opposed to `undefined`, which just means "still loading" or "no
  // bid selected". Only the confirmed-null case should fall back to the static catalog below.
  const noWorkflowYet = workflowQuery.data === null

  // The static skill catalog (no bid, no ACL) — every shipped workflow skill, "à faire" and
  // ready to invoke. Powers the slash-menu fallback below so the member never has to visit the
  // Dashboard and click "Create workflow" before a skill becomes usable; invoking one auto-
  // activates the workflow (see ensureWorkflow in the passerelle's generate-node.ts).
  const catalogQuery = useQuery({
    queryKey: ['workflow-catalog'],
    queryFn: () => api.workflowCatalog(),
    enabled: noWorkflowYet,
    staleTime: Infinity, // static template data — never changes at runtime
  })
  const catalogAsNodes: WorkflowNode[] = (catalogQuery.data?.nodes ?? []).map((n, i) =>
    catalogNodeAsWorkflowNode(n, i),
  )
  const wfNodes: WorkflowNode[] = noWorkflowYet ? catalogAsNodes : (workflowQuery.data?.nodes ?? [])

  // Only nodes that have a skill defined can be invoked from the chat — and, for the moment, only
  // the handful of skills `isSlashInvocable` allows (see lib/workflow.ts). The others remain part
  // of the workflow everywhere else; they are just not offered here.
  const invocableNodes: WorkflowNode[] = wfNodes.filter((n) => isSlashInvocable(n) && n.enabled)

  // navHidden nodes WITH a skill (proof-reading) → slash command only; no nav docs.
  // (export node has no skill — /export-word and /export-excel already cover it.)
  const actionNodes: WorkflowNode[] = wfNodes.filter(
    (n) => n.navHidden && isSlashInvocable(n) && n.enabled !== false,
  )

  // Built-in export commands always available when a bid is selected.
  const EXPORT_COMMANDS = [
    { nodeId: 'export-word',  section: t('export_word_label'),  kind: 'export' as const },
    { nodeId: 'export-excel', section: t('export_excel_label'), kind: 'export' as const },
  ]

  // User skills: loaded from the passerelle's user-skills/ directory.
  const userSkillsQuery = useQuery({
    queryKey: ['user-skills'],
    queryFn: () => api.userSkills(),
    staleTime: 60_000,
  })
  const userSkillItems = (userSkillsQuery.data?.skills ?? []).map((s: UserSkill) => ({
    nodeId: s.id,
    section: s.label,
    kind: 'user-skill' as const,
  }))

  // All slash-completable items: workflow skills + action nodes + export commands + user skills.
  // A navHidden node WITH a skill (proof-reading) satisfies both filters above, so it must be
  // listed once only — as the action, the variant carrying the right badge. Leaving both in
  // also produced two children with the same React key below, and the duplicated-key
  // reconciliation multiplied the row on every keystroke that re-filtered the menu.
  const actionNodeIds = new Set(actionNodes.map((n) => n.nodeId))
  const allMenuItems = [
    ...invocableNodes.filter((n) => !actionNodeIds.has(n.nodeId)).map((n) => ({ ...n, kind: 'skill' as const })),
    ...actionNodes.map((n) => ({ ...n, kind: 'action' as const })),
    ...(bid ? EXPORT_COMMANDS : []),
    ...userSkillItems,
  ]

  // Filter menu to what the user typed after '/' (only while no space yet).
  const slashPhase = draft.startsWith('/') && !draft.includes(' ')
  const slashFilter = slashPhase ? draft.slice(1).toLowerCase() : ''
  const menuItems = slashPhase
    ? allMenuItems.filter(
        (n) =>
          n.section.toLowerCase().includes(slashFilter) ||
          n.nodeId.toLowerCase().includes(slashFilter),
      )
    : []
  const skillMenuOpen = slashPhase && menuItems.length > 0

  // Back-compat alias used in JSX below
  const menuNodes = menuItems

  // When draft is "/nodeId <anything>" find the matched node for placeholder hint.
  const parsedSkillId = draft.startsWith('/') ? (draft.slice(1).split(' ')[0] ?? '') : ''
  const activeSkillNode = allMenuItems.find((n) => n.nodeId === parsedSkillId) ?? null

  // When a new part is opened, automatically re-target the chat to it (or to the whole bid
  // when no part is open). The chosen scope is not sticky across part changes.
  useEffect(() => { setScope(room ? 'part' : 'bid'); setScopeMenuOpen(false) }, [room])
  const roomRef = useRef(room)
  roomRef.current = room
  /** A finished run's widened target (AB#337), so a reload or another tab selects it too — only
   *  while the same part is open, since the target is relative to it. */
  const adoptWidenedScope = (job: AgentJob): void => {
    const widened = job.status === 'done' ? (job.result as AgentChatResult | undefined)?.scope : undefined
    if (widened && (job.room ?? null) === (roomRef.current ?? null)) setScope(widened)
  }

  // Close the scope menu when clicking outside of it.
  useEffect(() => {
    if (!scopeMenuOpen) return
    const onDown = (e: MouseEvent): void => {
      if (scopeMenuRef.current && !scopeMenuRef.current.contains(e.target as Node)) setScopeMenuOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [scopeMenuOpen])

  // Close the agent-picker menu when clicking outside of it.
  useEffect(() => {
    if (!agentMenuOpen) return
    const onDown = (e: MouseEvent): void => {
      if (agentMenuRef.current && !agentMenuRef.current.contains(e.target as Node)) setAgentMenuOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [agentMenuOpen])

  // Close the past-requests menu when clicking outside of it.
  useEffect(() => {
    if (!historyMenuOpen) return
    const onDown = (e: MouseEvent): void => {
      if (historyMenuRef.current && !historyMenuRef.current.contains(e.target as Node)) setHistoryMenuOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [historyMenuOpen])

  // Keep the list pinned to the bottom (the most recent request) whenever it's open, and whenever
  // it's re-filtered or a request finishes — same "stay anchored to the newest" idea as the main
  // feed's bottomRef, just instant instead of smooth (this is a small popover, not the transcript).
  useEffect(() => {
    if (!historyMenuOpen) return
    const el = historyListRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [historyMenuOpen, historyFilter, promptHistory])

  // The room actually sent to the agent: always the open part room (anchor for scoping).
  const effectiveRoom = room

  // Load the member's persisted choice. Claude Code defaults to Auto (AB#411); the other agents
  // to the preferred default, Opus 5.5 — see pickModel.
  // Clear the previous agent's list first so a slow/failed fetch can never show the wrong
  // agent's models (e.g. the Claude list lingering under Copilot).
  useEffect(() => {
    let alive = true
    setModels([])
    setModel('')
    setModelsLoading(true)
    api
      .agentModels(agent)
      .then(({ models: list }) => {
        if (!alive) return
        if (agent === 'Claude Code') migrateClaudeModelToAuto()
        const saved = getModel(agent)
        setModels(list)
        setModel(
          agent === 'Claude Code' && (!saved || !list.some((m) => m.id === saved)) ? AUTO_MODEL : pickModel(list, saved),
        )
        setModelsLoading(false)
      })
      .catch(() => {
        if (!alive) return
        setModels([])
        setModel('')
        setModelsLoading(false)
      })
    return () => {
      alive = false
    }
  }, [agent])

  function chooseModel(id: string): void {
    setModel(id)
    persistModel(agent, id)
  }

  // Auto is a web-side sentinel and never reaches a runner. Chat sends no model (the passerelle
  // routes it); the other runs (skills, export) are not routed and get the preferred Opus 5.5.
  const isAuto = model === AUTO_MODEL
  const runModel = (isAuto ? pickModel(models, null) : model) || undefined

  const anyRunning = items.some((i) => i.status === 'running')
  // Share the global agent lock: a running chat blocks other agent actions; conversely a message
  // typed while any agent (e.g. SVG) is busy waits in the queue below.
  const agentBusy = useAgentBusy().busy
  // True between a send leaving the composer and its job landing in `items` (the start request is
  // in flight): the queue must not pop a second prompt into that gap.
  const [dispatching, setDispatching] = useState(false)
  const busy = anyRunning || agentBusy || dispatching
  // Follow-ups typed while the agent works (FIFO, at most MAX_QUEUED). They live in lib/chat-queue.ts,
  // keyed by bid, so they outlive this panel (navigation, a collapsed chat) and each bid keeps its
  // own; memory only, emptied by Stop. Each setter writes to the bid of the render it came from.
  const queue = useChatQueue(bid)
  const setQueue = (update: QueuedPrompt[] | ((prev: QueuedPrompt[]) => QueuedPrompt[])): void =>
    setChatQueue(bid, update)
  // The bid whose in-flight chat jobs have been re-attached (see the per-bid effect below). Until
  // then a job still running from before a remount is not in `items` yet, so `busy` would wrongly
  // read false and the queue must not pop.
  const [reattachedBid, setReattachedBid] = useState<string | null | undefined>(undefined)
  const reattaching = reattachedBid !== bid
  const queueFull = queue.length >= MAX_QUEUED
  // Polite announcement for queueing ("Queued (1 of 2)" / "Queue full").
  const [queueNote, setQueueNote] = useState('')
  /** The one screen-reader announcement of a finished answer (AB#381). */
  const [answerNote, setAnswerNote] = useState('')
  const chatReleaseRef = useRef<null | (() => void)>(null)
  useEffect(() => {
    if (anyRunning && !chatReleaseRef.current) {
      chatReleaseRef.current = beginAgentTask()
    } else if (!anyRunning && chatReleaseRef.current) {
      chatReleaseRef.current()
      chatReleaseRef.current = null
    }
  }, [anyRunning])
  useEffect(() => () => chatReleaseRef.current?.(), [])
  // The conversational memory (and its size) that will accompany the next question.
  const ctx = useMemo(() => buildHistory(items), [items])
  const hasHistory = items.some((i) => i.kind === 'chat' && i.status === 'done' && i.message)

  /**
   * Which auth-failed item, if any, carries its own sign-in button — at most one, ever.
   *
   * Three conditions, and all of them are about not asking twice: the banner isn't already
   * offering the Claude sign-in; this is the LATEST auth failure (older recovered items are history, not a call to
   * action); and nothing has succeeded since (a later `done` run means the login came back, so the
   * old failure is stale). A second CTA would be harmless anyway — `POST /agent/login` de-dups per
   * owner and would just re-attach to the running job — but two sign-in buttons read as a bug.
   */
  const ctaItemId = useMemo(() => {
    if (signInAgent === 'claude') return null
    const lastAuthFail = items.map((it, i) => (it.authRequired ? i : -1)).filter((i) => i >= 0).pop()
    if (lastAuthFail === undefined) return null
    if (items.slice(lastAuthFail + 1).some((it) => it.status === 'done')) return null
    return items[lastAuthFail]!.id
  }, [items, signInAgent])

  /** Which update-required item carries the update button: the latest one, unless a later run
   *  succeeded (then the CLI evidently works again). Same "never ask twice" rule as ctaItemId. */
  const updateCtaItemId = useMemo(() => {
    const last = items.map((it, i) => (it.updateRequired ? i : -1)).filter((i) => i >= 0).pop()
    if (last === undefined) return null
    if (items.slice(last + 1).some((it) => it.status === 'done')) return null
    return items[last]!.id
  }, [items])

  const actionLabel = (tool: string): string =>
    tool === 'create_part' || tool === 'create_artefact' ? t('act_create')
      : tool === 'rename_part' ? t('act_rename')
      : tool === 'read_part' ? t('act_read')
      : tool === 'search_knowledge' ? t('act_search')
      : tool === 'search_corporate_knowledge' ? t('act_search_corporate')
      : tool === 'list_parts' ? t('act_list')
      : tool === 'reorder_parts' ? t('act_reorder')
      : tool === 'set_bid_site' ? t('act_pin_site')
      : tool === 'unset_bid_site' ? t('act_detach_site')
      // Not a Niobe tool: the passerelle borrows this channel to report the transient-refresh wait
      // (ProgressEvent has no non-tool shape). Without this branch the fallback below would tell
      // the member the agent "updated" something, on a run whose precondition is that nothing ran.
      : tool === 'retry' ? t('act_retry')
      : t('act_write')
  const shortTarget = (room?: string): string => {
    if (!room) return ''
    // For search queries keep them readable but cap length; for part paths take the last segment.
    const seg = room.split('/').slice(-1)[0] ?? room
    const text = seg === room ? room : seg   // query strings won't contain '/', part paths will
    return text.length > 48 ? text.slice(0, 46) + '…' : text
  }
  const scopeLabel = (s: ChatScope, r: string | null): string =>
    s === 'part' ? (r ? shortTarget(r) : t('scope_part')) : s === 'artefact' ? t('scope_artefact') : t('scope_bid')

  // The chat context is scoped PER BID: switching bid loads that bid's conversation (and thus its
  // memory) and never the previous bid's. Also re-attaches the bid's in-flight/recent chat jobs
  // after a refresh (the passerelle keeps them while it stays up).
  useEffect(() => {
    setItems([]) // drop the previous bid's conversation immediately (its queue stays in its slot)
    setQueueNote('')
    if (!bid) {
      setReattachedBid(bid)
      return
    }
    let alive = true
    api
      .agentJobs({ bid })
      .then(({ jobs }) => {
        if (!alive) return
        setReattachedBid(bid)
        const chat = jobs.filter((j) => j.kind === 'chat').sort((a, b) => a.createdAt.localeCompare(b.createdAt))
        if (chat.length === 0) return
        adoptWidenedScope(chat[chat.length - 1]!)
        // Merge so a message sent before this resolved is kept; dedup by job id.
        setItems((prev) => {
          const have = new Set(prev.map((i) => i.jobId).filter(Boolean))
          const fresh = chat.filter((j) => !have.has(j.id)).map(buildItem)
          return [...fresh, ...prev]
        })
      })
      .catch(() => {
        if (alive) setReattachedBid(bid)
      })
    return () => {
      alive = false
    }
  }, [bid])

  // Poll the running jobs until they finish; patch their line in place (keep the typed q).
  const runningKey = items
    .filter((i) => i.status === 'running' && i.jobId)
    .map((i) => i.jobId)
    .join(',')
  useEffect(() => {
    if (!runningKey) return
    const ids = runningKey.split(',')
    let alive = true
    const tick = async (): Promise<void> => {
      for (const id of ids) {
        try {
          const job = await api.agentJob(id)
          if (!alive) continue
          if (job.status === 'running') {
            // Still in progress — patch progress steps and any pending lock question into the item
            // without changing status. A question already answered here is not shown again while
            // the passerelle catches up.
            const ask = job.ask && !answeredAsks.current.has(job.ask.id) ? job.ask : undefined
            setItems((prev) =>
              prev.map((it) => {
                if (it.jobId !== id) return it
                const { ask: _old, partialText: _text, ...rest } = it
                return {
                  ...rest,
                  ...(job.progress?.length ? { progress: job.progress } : {}),
                  ...(job.partialText ? { partialText: job.partialText } : {}),
                  ...(ask ? { ask } : {}),
                }
              }),
            )
            continue
          }
          // Keep the typed question AND its attachment thumbnails: the job carries neither back
          // (the passerelle stores the prompt, not the image bytes).
          setItems((prev) =>
            prev.map((it) => (it.jobId === id ? { ...buildItem(job), q: it.q, ...(it.images ? { images: it.images } : {}), ...(it.decks ? { decks: it.decks } : {}) } : it)),
          )
          // Past-requests dropdown: the "heure de fin" is the job's own finishedAt (passerelle
          // truth), falling back to now if it's somehow missing.
          finishPrompt(id, job.finishedAt ? new Date(job.finishedAt).getTime() : Date.now())
          // Screen readers hear the answer once it is complete — never chunk by chunk while it
          // streams (the partial text sits outside every live region).
          if (job.status === 'done') setAnswerNote(t('chat_answer_ready'))
          // The agent may have created/renamed/written parts — refresh the outline so the
          // left nav and bid overview reflect it immediately (no manual page reload).
          adoptWidenedScope(job)
          if (job.status === 'done' && bid) {
            void qc.invalidateQueries({ queryKey: ['docs', bid] })
            // …or pinned/detached the bid site — refresh the BidSiteCard row the same way.
            void qc.invalidateQueries({ queryKey: ['bid-site', bid] })
          }
          // The run just proved the login is dead, and the passerelle dropped its cached answer at
          // the same moment. Re-read it now so the sign-in banner appears without a reload, rather
          // than waiting out the query's own staleTime.
          // Same for an update-required failure: the passerelle has just learned the higher floor.
          if (job.errorCode === 'agent_auth_required' || job.errorCode === 'agent_update_required') {
            void qc.invalidateQueries({ queryKey: ['agent-status'] })
          }
        } catch {
          /* transient — keep polling */
        }
      }
    }
    // Chained rather than setInterval: at POLL_MS a slow passerelle must not stack overlapping ticks.
    let h: ReturnType<typeof setTimeout> | undefined
    const loop = async (): Promise<void> => {
      await tick()
      if (alive) h = setTimeout(() => void loop(), POLL_MS)
    }
    void loop()
    return () => {
      alive = false
      clearTimeout(h)
    }
  }, [runningKey])

  // Auto-scroll to the bottom when a new AI message appears or completes.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [items])

  function autosize(): void {
    const ta = inputRef.current
    if (!ta) return
    ta.style.height = 'auto'
    ta.style.height = `${Math.min(ta.scrollHeight, 160)}px`
  }

  // A draft restored from lib/chat-draft.ts — on mount, or when another bid's entry swaps in — is
  // sized to its content like a typed one. It is the member's own text, not a recalled prompt, so
  // any recall walk ends here and ArrowUp/ArrowDown keep behaving as they always have.
  useLayoutEffect(() => {
    autosize()
    setRecallIdx(-1)
  }, [bid])

  function pushItem(item: ChatItem): void {
    setItems((s) => [...s, item])
  }

  /** Push a new chat line AND record it in the past-requests dropdown (chat-prompt-history.ts),
   *  linked by jobId so its "heure de fin" gets filled in once the job resolves (see the polling
   *  effect above and stopRunning below). A jobId-less item (an immediate client-side error, e.g.
   *  no bid selected) is recorded as already-ended. */
  function pushAndRecord(item: ChatItem): void {
    // A fresh question: clear the last answer's announcement, so this one's is heard again.
    setAnswerNote('')
    pushItem(item)
    recordPrompt(item.q, item.jobId)
  }

  /** Auto-complete the nodeId into the textarea: "/nodeId " — leaves room for an instruction.
   *  For export commands (no instruction needed) we fire immediately. */
  function completeSkill(item: { nodeId: string; kind: string }): void {
    if (item.kind === 'export') {
      // Export commands fire immediately — set the draft and send at once
      const exportDraft = `/${item.nodeId}`
      setDraft('')
      if (inputRef.current) inputRef.current.style.height = 'auto'
      const format = item.nodeId === 'export-word' ? 'word' : 'excel'
      // Same block as send()'s: this fast path calls api.runExport directly and never goes
      // through send(), so it needs its own guard.
      if (bid && !anyRunning && !signInBlocking) {
        void api.runExport(bid, format as 'word' | 'excel', effectiveRoom, scope, agent)
          .then(({ jobId }) => pushAndRecord({ id: jobId, jobId, kind: 'chat', q: exportDraft, status: 'running' }))
          .catch((e) => pushAndRecord({ id: crypto.randomUUID(), kind: 'note', q: exportDraft, status: 'error', note: e instanceof Error ? e.message : 'Export failed.' }))
      }
      return
    }
    const newDraft = `/${item.nodeId} `
    setDraft(newDraft)
    autosize()
    inputRef.current?.focus()
  }

  /** Forget the conversation: empties the chat and resets the context sent to the LLM. Note this
   *  does NOT touch the past-requests recall list (chat-prompt-history.ts) — that's the point. */
  function clearChat(): void {
    setItems([])
  }

  /**
   * What to put back in the composer when retrying a turn that died on a dead sign-in.
   *
   * Usually the question verbatim. A skill invocation is the exception: the passerelle stores it
   * as the LABEL `/<skill>: <instruction>`, which typed back as-is would be read as plain chat —
   * so it is turned back into the `/<skill> <instruction>` the slash router understands.
   *
   * There is deliberately no automatic re-send: an item doesn't carry the agent/model/scope/KB
   * toggles the original message was sent with, and silently re-running under different settings
   * is worse than handing the member their sentence back.
   */
  function retryDraft(q: string): string {
    const m = /^\/([^\s:]+): ([\s\S]*)$/.exec(q)
    return m ? `/${m[1]} ${m[2]}` : q
  }

  /** Resend a failed turn as-is (the update card's Retry). While another run is busy it lands in
   *  the composer instead, where the member can send it once the agent is free. */
  function resendItem(item: ChatItem): void {
    const text = retryDraft(item.q)
    if (busy) return recallDraft(text)
    void send({ id: crypto.randomUUID(), text, images: item.images ?? [], ...(item.decks?.length ? { decks: item.decks } : {}) })
  }

  /** Write a recalled/selected past request into the composer without sending it (same
   *  draft-population pattern as completeSkill — the member can edit before sending). */
  function recallDraft(q: string): void {
    setDraft(q)
    autosize()
    inputRef.current?.focus()
  }

  /**
   * Stop button: mark every running turn 'cancelled' immediately (client-side — the UI must not
   * wait on a round trip to react), then ask the passerelle to cancel the underlying job, which
   * aborts the in-flight local-agent SDK call itself. Best-effort: a failed/late cancel call
   * changes nothing here, since the item is already shown as cancelled.
   */
  // Lock questions answered from this panel, so a poll racing the answer does not bring one back.
  const answeredAsks = useRef(new Set<string>())

  /** Answer a paused run's lock or scope question; the card goes at once, the run resumes on the
   *  passerelle. Widen also switches the target selector, so it stays selected for the next prompts. */
  function answerAsk(item: ChatItem, choice: AgentLockChoice | AgentScopeChoice): void {
    const ask = item.ask
    if (!ask || !item.jobId) return
    answeredAsks.current.add(ask.id)
    if (ask.kind === 'scope' && choice === 'widen') setScope(ask.to)
    setItems((prev) =>
      prev.map((it) => {
        if (it.id !== item.id) return it
        const { ask: _answered, ...rest } = it
        return rest
      }),
    )
    // 409 = no longer pending (it timed out on the passerelle meanwhile): the run already moved on.
    void api.answerJob(item.jobId, ask.id, choice).catch(() => undefined)
  }

  function stopRunning(): void {
    // Stop means "pause": the waiting follow-ups go too (this bid's only), so nothing auto-runs
    // behind the member.
    setQueue([])
    setQueueNote('')
    const runningJobIds = items.filter((it) => it.status === 'running' && it.jobId).map((it) => it.jobId!)
    if (runningJobIds.length === 0) return
    setItems((prev) => prev.map((it) => (it.status === 'running' ? { ...it, status: 'cancelled' } : it)))
    const cancelledAt = Date.now()
    for (const jobId of runningJobIds) {
      finishPrompt(jobId, cancelledAt) // client-side cancel — don't wait on the passerelle's own finishedAt
      void api.cancelJob(jobId).catch(() => undefined)
    }
  }

  /**
   * Send the composer's draft — or, when `queued` is given, a follow-up popped from the queue (the
   * composer is then left alone). While the agent works the draft is queued instead of sent; it is
   * routed (slash skill, KB chips, model, open part) only once popped and back here.
   */
  async function send(queued?: QueuedPrompt): Promise<void> {
    const raw = (queued ? queued.text : draft).trim()
    // An attached image is a message on its own — the agent is asked to look at it; so is a deck.
    const attached = queued ? queued.images : attachments
    const decks = queued ? (queued.decks ?? []) : attachedDecks
    if (!raw && attached.length === 0 && decks.length === 0) return
    // Nothing can leave while the sign-in banner is up: every path below would fail on the local
    // agent anyway. Guarded HERE rather than only on the Send button, so Enter is covered too —
    // and the textarea stays editable, because Retry fills it.
    if (signInBlocking) return
    // A deck still uploading is not attached yet: the message waits for it rather than leave without it.
    if (!queued && stagingDecks.length > 0) {
      setAttachError(t('chat_deck_wait'))
      return
    }
    if (!queued && (busy || queue.length > 0)) {
      // The agent is working (or earlier follow-ups still wait their turn): queue it, FIFO.
      if (queueFull) {
        setQueueNote(t('chat_queue_full'))
        return // the draft stays in the composer
      }
      setQueue((q) => [...q, { id: crypto.randomUUID(), text: draft, images: attachments, ...(attachedDecks.length ? { decks: attachedDecks } : {}) }])
      setQueueNote(t('chat_queued').replace('{n}', String(queue.length + 1)).replace('{max}', String(MAX_QUEUED)))
      setDraft('')
      setAttachments([])
      setAttachedDecks([])
      if (inputRef.current) inputRef.current.style.height = 'auto'
      setRecallIdx(-1)
      setAttachError(null)
      return
    }
    if (busy) return
    if (!queued) {
      setDraft('') // also forgets the stored draft text, so navigating back finds the composer empty
      if (inputRef.current) inputRef.current.style.height = 'auto'
      setRecallIdx(-1) // a real submit exits shell-style recall
    }
    setAttachError(null)
    setDispatching(true)
    try {
      await dispatch(raw, attached, decks, !!queued)
    } finally {
      setDispatching(false)
    }
  }

  /** Route one message (typed, or popped from the queue) to the right agent job. */
  async function dispatch(raw: string, attached: ChatImage[], decks: ChatDeck[], fromQueue: boolean): Promise<void> {

    if (!bid) {
      pushAndRecord({ id: crypto.randomUUID(), kind: 'note', q: raw, status: 'error', note: 'No bid selected.' })
      return
    }

    // Slash-skill invocation: "/<nodeId> [instruction]" — route to runSkill.
    // Slash-export:           "/export-word" or "/export-excel" — route to runExport.
    if (raw.startsWith('/')) {
      const spaceIdx = raw.indexOf(' ')
      const nodeId = spaceIdx === -1 ? raw.slice(1) : raw.slice(1, spaceIdx)
      const instruction = spaceIdx === -1 ? '' : raw.slice(spaceIdx + 1).trim()
      // The KB chips reach skills too; an untouched Corp KB is omitted so the skill's default applies.
      const skillKb = { bidKb, ...(corporateKb !== null ? { corporateKb } : {}) }

      // A slash command runs a fixed skill/export prompt — it has no channel for an attachment.
      // Say so and KEEP the images in the composer rather than dropping them silently; an unknown
      // command falls through to the plain-chat path below, which does carry them.
      const routedAsCommand =
        nodeId === 'export-word' || nodeId === 'export-excel' ||
        invocableNodes.some((n) => n.nodeId === nodeId) ||
        actionNodes.some((n) => n.nodeId === nodeId) ||
        userSkillItems.some((s) => s.nodeId === nodeId)
      if (routedAsCommand && (attached.length > 0 || decks.length > 0)) {
        setAttachError(t('chat_img_slash'))
        // A queued command's attachments already left the composer — hand them back.
        if (fromQueue) {
          setAttachments((prev) => [...prev, ...attached].slice(0, MAX_CHAT_IMAGES))
          setAttachedDecks((prev) => [...prev, ...decks].slice(0, MAX_CHAT_DECKS))
        }
      }

      // Export command
      if (nodeId === 'export-word' || nodeId === 'export-excel') {
        const format = nodeId === 'export-word' ? 'word' : 'excel'
        try {
          const { jobId } = await api.runExport(bid, format, effectiveRoom, scope, agent, runModel)
          pushAndRecord({ id: jobId, jobId, kind: 'chat', q: raw, status: 'running' })
        } catch (e) {
          pushAndRecord({ id: crypto.randomUUID(), kind: 'note', q: raw, status: 'error', note: e instanceof Error ? e.message : 'Export failed.' })
        }
        return
      }

      // Skill invocation
      const matchedNode = invocableNodes.find((n) => n.nodeId === nodeId)
      if (matchedNode) {
        try {
          const { jobId } = await api.runSkill(bid, matchedNode.nodeId, instruction, effectiveRoom, scope, agent, runModel, skillKb)
          pushAndRecord({ id: jobId, jobId, kind: 'chat', q: raw, status: 'running' })
        } catch (e) {
          pushAndRecord({
            id: crypto.randomUUID(),
            kind: 'note',
            q: raw,
            status: 'error',
            note: e instanceof Error ? e.message : 'The local agent failed.',
          })
        }
        return
      }

      // Action node invocation (navHidden: coherence, export…) — routed as a skill call
      const matchedAction = actionNodes.find((n) => n.nodeId === nodeId)
      if (matchedAction) {
        try {
          const { jobId } = await api.runSkill(bid, matchedAction.nodeId, instruction, effectiveRoom, scope, agent, runModel, skillKb)
          pushAndRecord({ id: jobId, jobId, kind: 'chat', q: raw, status: 'running' })
        } catch (e) {
          pushAndRecord({
            id: crypto.randomUUID(),
            kind: 'note',
            q: raw,
            status: 'error',
            note: e instanceof Error ? e.message : 'The local agent failed.',
          })
        }
        return
      }

      // User skill invocation — independent of the workflow
      const matchedUserSkill = userSkillItems.find((s) => s.nodeId === nodeId)
      if (matchedUserSkill) {
        try {
          const { jobId } = await api.runUserSkill(bid, matchedUserSkill.nodeId, instruction, effectiveRoom, scope, agent, runModel, skillKb)
          pushAndRecord({ id: jobId, jobId, kind: 'chat', q: raw, status: 'running' })
        } catch (e) {
          pushAndRecord({
            id: crypto.randomUUID(),
            kind: 'note',
            q: raw,
            status: 'error',
            note: e instanceof Error ? e.message : 'The local agent failed.',
          })
        }
        return
      }
    }

    // The local agent handles the prompt: it can read, modify, create and rename parts via
    // its tools (it can never delete). One unified path — no client-side edit/ask routing.
    // Carry the prior chat turns (capped) so the agent has conversational memory.
    // Attachments belong to THIS message only — clear them as it leaves, whatever the outcome.
    const prompt = raw || (decks.length ? t('chat_deck_only') : t('chat_img_only'))
    if (!fromQueue) {
      setAttachments([])
      setAttachedDecks([])
    }
    // The staged decks travel as { stagedId, name } only; the passerelle tells the agent to import_pptx them.
    const sent = decks.length ? { decks } : {}
    try {
      const { turns } = buildHistory(items)
      const { jobId } = await api.agentChat(
        bid, effectiveRoom, prompt, agent, isAuto ? undefined : model || undefined, turns, scope, bidKb, corporateKb ?? false, localSkillsEnabled, disabledSkills,
        attached.length ? attached : undefined, threadSessionId(items),
        { modelIsDefault: isAuto, ...(effectiveRoom && partKind ? { partKind } : {}), ...sent },
      )
      pushAndRecord({ id: jobId, jobId, kind: 'chat', q: prompt, status: 'running', ...(attached.length ? { images: attached } : {}), ...sent })
    } catch (e) {
      pushAndRecord({
        id: crypto.randomUUID(),
        kind: 'note',
        q: prompt,
        status: 'error',
        note: e instanceof Error ? e.message : 'The local agent failed.',
        ...(attached.length ? { images: attached } : {}),
        ...sent,
      })
    }
  }

  /**
   * "Redo with Opus" (AB#401): re-send a routed run's prompt on Claude's default model. No model is
   * sent and `redoOf` is set, so the passerelle runs Opus 5.5 with no routing, and the redo's usage
   * row points back at the routed run.
   */
  async function redoWithOpus(item: ChatItem): Promise<void> {
    if (!bid || busy) return
    setDispatching(true)
    try {
      const { turns } = buildHistory(items)
      const { jobId } = await api.agentChat(
        bid, effectiveRoom, item.q, 'Claude Code', undefined, turns, scope, bidKb, corporateKb ?? false, localSkillsEnabled, disabledSkills,
        item.images?.length ? item.images : undefined, threadSessionId(items),
        { redoOf: item.usageId ?? item.jobId ?? item.id, ...(effectiveRoom && partKind ? { partKind } : {}), ...(item.decks?.length ? { decks: item.decks } : {}) },
      )
      pushAndRecord({ id: jobId, jobId, kind: 'chat', q: item.q, status: 'running', ...(item.images?.length ? { images: item.images } : {}), ...(item.decks?.length ? { decks: item.decks } : {}) })
    } catch (e) {
      pushAndRecord({ id: crypto.randomUUID(), kind: 'note', q: item.q, status: 'error', note: e instanceof Error ? e.message : 'The local agent failed.' })
    } finally {
      setDispatching(false)
    }
  }

  // Auto-run the queue: once nothing is busy (this chat's job ended — done, error or no-op — and no
  // other agent surface holds the lock), the oldest follow-up goes through the normal send() path.
  // Nothing pops while the panel is unmounted; on remount it waits for the bid's running jobs to be
  // re-attached, then routes against the part open at that moment.
  useEffect(() => {
    if (busy || signInBlocking || reattaching || queue.length === 0) return
    // Pop from the store, and only the head this render saw: a re-run of this effect with the same
    // render's state (StrictMode) must not pop and send the next entry too.
    const [next, ...rest] = getChatQueue(bid)
    if (!next || next.id !== queue[0]!.id) return
    setQueue(rest)
    setQueueNote('')
    void send(next)
    // send() is re-created every render; the state it reads is the one this effect just saw.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy, signInBlocking, reattaching, queue])

  // No CLI at all on this machine: there is nothing to sign into and nothing to say, so the whole
  // panel is replaced with an explanation and a link to the one place that can fix it.
  //
  // "Installed but signed out" used to land here too. It no longer does: that is a recoverable
  // state, and hiding the transcript to say so cost the member their conversation and offered them
  // only a trip to Settings. It is now the in-panel banner further down — same strings, but the
  // sign-in happens right there and the transcript stays put.
  if (noAgentDetected) {
    return (
      <aside className="assistant" aria-label="Niobe chat">
        <div className="as-head">
          <div><b>Niobe chat</b></div>
          {onCollapse && (
            <button className="as-collapse" aria-label={t('collapse_chat')} title={t('collapse_chat')} onClick={onCollapse}>
              ›
            </button>
          )}
        </div>
        <div className="chat-empty-noagent">
          <p className="chat-empty-noagent-title">{t('chat_no_agent_title')}</p>
          <p>{t('chat_no_agent_body')}</p>
          <button type="button" className="mini ok" onClick={() => navigate(aiAgentSettingsPath)}>
            {t('chat_no_agent_cta')}
          </button>
        </div>
      </aside>
    )
  }

  return (
    <aside className="assistant" aria-label="Niobe chat">
      <div className="as-head">
        {/* Agent picker: click the logo to choose which local agent powers this chat — mirrors
            the "Target scope selector" dropdown pattern below (chat-target-scope), just anchored
            to the chat logo instead of the composer. */}
        <div className="agent-logo" ref={agentMenuRef}>
          <button
            type="button"
            className="nm"
            aria-haspopup="menu"
            aria-expanded={agentMenuOpen}
            aria-label={t('ai_drv')}
            title="Pick the local agent that powers this chat."
            onClick={() => setAgentMenuOpen((o) => !o)}
          >
            ◆
          </button>
          {agentMenuOpen && (
            <ul className="agent-logo-menu" role="menu" aria-label={t('ai_drv')}>
              {AGENTS.map((a) => {
                // Unusable agents (not detected at all, or detected but definitely signed out —
                // Claude, or Copilot once its status read has answered) stay listed (so the member
                // can see what Niobe knows about this machine) but aren't selectable — picking one
                // would just fail on send.
                // Optimistically "usable" while the status check is still in flight, same care
                // as the empty state above: never flash everything as unavailable on the way to
                // a normal "yes".
                const id = AGENT_ID[a]
                const detail = agentStatusQuery.data?.agents[id]
                const usable = agentStatusQuery.data === undefined || isAgentUsable(detail, id)
                const needsLogin = needsSignIn(detail, id)
                return (
                  <li role="none" key={a}>
                    <button
                      type="button"
                      role="menuitemradio"
                      aria-checked={agent === a}
                      disabled={!usable}
                      title={usable ? undefined : needsLogin ? t('chat_agent_not_authenticated') : t('chat_agent_not_detected')}
                      className={agent === a ? 'active' : ''}
                      onClick={() => { setAgent(a); persistAgent(a); setAgentMenuOpen(false) }}
                    >
                      {AGENT_LABEL[a]}
                      {agent === a && <span className="ok" />}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
        <div>
          <b>Niobe chat</b>
        </div>
        {onCollapse && (
          <button className="as-collapse" aria-label={t('collapse_chat')} title={t('collapse_chat')} onClick={onCollapse}>
            ›
          </button>
        )}
      </div>

      <div className="agentpick">
        {modelsLoading && <span className="model-loading">{t('loading')}</span>}
        {!modelsLoading && models.length > 0 && (
          <select
            className="model-pick"
            value={model}
            aria-label="Model"
            title="Model"
            onChange={(e) => chooseModel(e.target.value)}
          >
            {agent !== 'Claude Code'
              ? groupModels(models).map((g) => (
                  <optgroup key={g.label} label={g.label}>
                    {g.items.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </optgroup>
                ))
              : [
                  <option key={AUTO_MODEL} value={AUTO_MODEL} title={t('chat_model_auto_title')}>
                    {t('chat_model_auto')}
                  </option>,
                  ...models.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  )),
                ]}
          </select>
        )}
      </div>

      <div className="stream">
        {items.length === 0 && (
          <div className="ans">
            <div className="body" style={{ color: 'var(--muted)' }}>{t('ph')}</div>
          </div>
        )}
        {items.map((e) => (
          <div key={e.id} style={{ display: 'contents' }}>
            <div className="ask">
              {e.q}
              {((e.images?.length ?? 0) > 0 || (e.decks?.length ?? 0) > 0) && (
                <div className="chat-attach-sent">
                  {e.images?.map((img, ii) => (
                    <img key={ii} className="chat-attach-thumb" src={chatImageSrc(img)} alt={img.name} title={img.name} />
                  ))}
                  {e.decks?.map((d) => (
                    <span key={d.stagedId} className="chat-attach">
                      <span className="chat-attach-deck" aria-hidden="true"><FileIcon name={d.name} /></span>
                      <span className="chat-attach-name" title={d.name}>{d.name}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="ans">
              <div className="who"><span className="nm">◆</span> <b>{agent}</b></div>
              <div className="body">
                {e.status === 'running' && (
                  <>
                    <Thinking label={t('working')} />
                    {(e.progress?.length ?? 0) > 0 && (
                      <div className="chat-progress" aria-live="polite">
                        {(() => {
                          // Deduplicate by (tool, target) — keep the latest status for each pair.
                          const seen = new Map<string, AgentProgressEvent>()
                          for (const p of e.progress!) seen.set(`${p.tool}::${p.target ?? ''}`, p)
                          return [...seen.values()].map((p, pi) => (
                            <span className={`chat-progress-step ${p.status === 'start' ? 'running' : 'done'}`} key={pi}>
                              {p.status === 'start' ? '⟳' : '✓'} {actionLabel(p.tool)}{shortTarget(p.target) ? ` · ${shortTarget(p.target)}` : ''}
                            </span>
                          ))
                        })()}
                      </div>
                    )}
                    {/* The answer as it is written (AB#381). Deliberately NOT in a live region:
                        the completed answer is announced once, through answerNote. */}
                    {e.partialText && <div className="chat-md">{renderChatMarkdown(e.partialText)}</div>}
                    {e.ask &&
                      (e.ask.kind === 'scope' ? (
                        <ScopeAskCard ask={e.ask} onAnswer={(choice) => answerAsk(e, choice)} />
                      ) : (
                        <LockAskCard ask={e.ask} onAnswer={(choice) => answerAsk(e, choice)} />
                      ))}
                  </>
                )}
                {e.status === 'error' && e.authRequired && (
                  <div className="chat-auth-failed" role="status" aria-live="polite">
                    <p className="chat-auth-failed-title">{t('chat_auth_required_title')}</p>
                    {/* The CLI's own sentence, shown as-is. Never parsed: whatever wording a given
                        Claude Code version uses, it is more informative than anything we'd infer. */}
                    {e.note && <p className="chat-auth-failed-reason">{e.note}</p>}
                    {/* A rejected long-lived token needs one extra sentence: the dead token lives
                        in Settings and nothing here can clear it. The sign-in below still stands —
                        with the token no longer injected, it is a real fix. */}
                    {tokenRejected && <p className="chat-auth-failed-reason">{t('chat_auth_token_rejected')}</p>}
                    <div className="chat-auth-failed-actions">
                      <button type="button" className="mini ok" onClick={() => recallDraft(retryDraft(e.q))}>
                        {t('chat_auth_required_retry')}
                      </button>
                    </div>
                    {/* Sign in from right here when the banner isn't already offering it — which
                        happens whenever /agent/status still says "signed in" (oauth_org_not_allowed,
                        or an older CLI that doesn't rewrite its credentials on a failed refresh).
                        At most one of these is ever shown; see ctaItemId. */}
                    {e.id === ctaItemId && (
                      <ClaudeLoginCta onSignedIn={() => void qc.invalidateQueries({ queryKey: ['agent-status'] })} />
                    )}
                  </div>
                )}
                {e.status === 'error' && e.updateRequired && (
                  <ChatUpdateRequiredCard
                    requiredVersion={e.updateRequired.requiredVersion}
                    currentVersion={e.updateRequired.currentVersion}
                    showCta={e.id === updateCtaItemId}
                    onUpdated={() => void qc.invalidateQueries({ queryKey: ['agent-status'] })}
                    onRetry={() => resendItem(e)}
                  />
                )}
                {e.status === 'error' && !e.authRequired && !e.updateRequired && <span style={{ color: 'var(--red)' }}>{e.note}</span>}
                {e.status === 'cancelled' && <span style={{ color: 'var(--muted)' }}>{t('chat_cancelled')}</span>}
                {e.status === 'done' && e.kind === 'note' && <span>{e.note}</span>}
                {e.status === 'done' && e.kind === 'chat' && (
                  <>
                    <div className="chat-md">{renderChatMarkdown(e.message ?? '')}</div>
                    {(e.undone?.length ?? 0) > 0 && <UndoneList undone={e.undone!} />}
                    {extractDownloadUrls(e.message ?? '').map(({ url, filename }) => (
                      <button
                        key={url}
                        className="chat-download-btn"
                        onClick={() => void downloadBinary(url, { fallbackName: filename })}
                        title={filename}
                      >
                        ⇩ {filename}
                      </button>
                    ))}
                    {(e.actions?.length ?? 0) > 0 && (
                      <div className="chat-actions">
                        {e.actions!.map((a, ai) => (
                          <span className="chat-action" key={ai}>
                            ✦ {shortTarget(a.target)} {actionLabel(a.tool)}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="chat-metrics">
                      ⟳ {(e.tokensIn ?? 0) + (e.tokensOut ?? 0) > 0 ? `${(e.tokensIn ?? 0) + (e.tokensOut ?? 0)} ${t('chat_tokens')} · ` : ''}
                      {((e.durationMs ?? 0) / 1000).toFixed(1)}s
                      {e.model && <span className="chat-model" title={t('chat_model_title')}>· ◆ {e.model}</span>}
                      {e.routed && (
                        <button
                          type="button"
                          className="chat-redo-btn"
                          title={t('chat_redo_opus_title')}
                          disabled={busy}
                          onClick={() => void redoWithOpus(e)}
                        >
                          <span aria-hidden="true">↻</span> {t('chat_redo_opus')}
                        </button>
                      )}
                      {/* Only when a search actually returned something — never a "0 sources" that
                          would read as "the agent found nothing" rather than "it did not look". */}
                      {(e.sources?.length ?? 0) > 0 && (
                        <button
                          type="button"
                          className="chat-sources-btn"
                          aria-haspopup="dialog"
                          aria-label={`${e.sources!.length} ${t('chat_sources_aria')}`}
                          onClick={() => setOpenSources(e.sources!)}
                        >
                          📎 {e.sources!.length} {t('chat_sources')}
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {openSources && <SourcesModal sources={openSources} onClose={() => setOpenSources(null)} />}

      {/* The sign-in banner, where the full-panel swap used to be. It sits between the transcript
          and the composer: the conversation stays readable, sending is blocked (see send()), and
          the fix is one button away instead of a trip to Settings — which is kept as the secondary
          link, since that page is still where the member manages the agent itself. */}
      {signInAgent && (
        <div className="chat-signin-banner" role="status" aria-live="polite">
          <p className="chat-signin-banner-title">{t('chat_agent_signin_title')}</p>
          <p>{t(signInAgent === 'copilot' ? 'chat_agent_signin_body_copilot' : 'chat_agent_signin_body')}</p>
          {signInAgent === 'copilot' ? (
            <CopilotLoginCta onSignedIn={() => void qc.invalidateQueries({ queryKey: ['agent-status'] })} />
          ) : (
            <ClaudeLoginCta onSignedIn={() => void qc.invalidateQueries({ queryKey: ['agent-status'] })} />
          )}
          <button type="button" className="mini ghost" onClick={() => navigate(aiAgentSettingsPath)}>
            {t('chat_no_agent_cta')}
          </button>
        </div>
      )}

      <div className="composer">
        <div className="chat-target">
          {/* ── Target scope selector ──────────────────────────────── */}
          <div className="chat-target-scope" ref={scopeMenuRef}>
            <button
              type="button"
              className="chat-target-trigger"
              aria-haspopup="menu"
              aria-expanded={scopeMenuOpen}
              title={t('chat_target_lbl')}
              onClick={() => setScopeMenuOpen((o) => !o)}
            >
              <span className="chat-target-lbl">{t('chat_target_lbl')}: </span>
              <span className="chat-target-name">{scopeLabel(scope, room)}</span>
              <span className="chat-target-caret" aria-hidden="true">▾</span>
            </button>
            {scopeMenuOpen && (
              <ul className="chat-target-menu" role="menu">
                <li role="none">
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={scope === 'part'}
                    className={scope === 'part' ? 'active' : ''}
                    disabled={!room}
                    onClick={() => { setScope('part'); setScopeMenuOpen(false) }}
                  >{t('scope_part')}{room ? ` · ${shortTarget(room)}` : ''}</button>
                </li>
                <li role="none">
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={scope === 'artefact'}
                    className={scope === 'artefact' ? 'active' : ''}
                    disabled={!room}
                    onClick={() => { setScope('artefact'); setScopeMenuOpen(false) }}
                  >{t('scope_artefact')}</button>
                </li>
                <li role="none">
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={scope === 'bid'}
                    className={scope === 'bid' ? 'active' : ''}
                    onClick={() => { setScope('bid'); setScopeMenuOpen(false) }}
                  >{t('scope_bid')}</button>
                </li>
              </ul>
            )}
          </div>
          {/* ── Past-requests recall: independent of the visible transcript, survives
              "Forget the conversation" and bid switches (see lib/chat-prompt-history.ts). ── */}
          <div className="chat-history" ref={historyMenuRef}>
            <button
              type="button"
              className="chat-history-trigger"
              aria-haspopup="listbox"
              aria-expanded={historyMenuOpen}
              aria-label={t('chat_history_lbl')}
              title={t('chat_history_lbl')}
              onClick={() => {
                setHistoryFilter('')
                setHistoryMenuIdx(Number.MAX_SAFE_INTEGER) // defaults to the most recent one
                setHistoryMenuOpen((o) => !o)
              }}
            >
              <span aria-hidden="true">↺</span>
            </button>
            {historyMenuOpen && (
              <div className="chat-history-menu">
                {/* Oldest at the top, most recent at the bottom — right above the search box —
                    like a normal chat log. See displayHistory/activeHistoryIdx above. */}
                <div
                  id="chat-history-listbox"
                  className="chat-history-list"
                  role="listbox"
                  aria-label={t('chat_history_lbl')}
                  ref={historyListRef}
                >
                  {displayHistory.length === 0 ? (
                    <div className="chat-history-empty">
                      {promptHistory.length === 0 ? t('chat_history_empty') : t('chat_history_no_match')}
                    </div>
                  ) : (
                    displayHistory.map((p, i) => (
                      <div
                        key={p.id}
                        className={'chat-history-item' + (i === activeHistoryIdx ? ' sel' : '')}
                        role="option"
                        aria-selected={i === activeHistoryIdx}
                        title={p.q}
                        onMouseEnter={() => setHistoryMenuIdx(i)}
                        onMouseDown={(e) => {
                          e.preventDefault() // keep focus on the search input
                          recallDraft(p.q)
                          setHistoryMenuOpen(false)
                        }}
                      >
                        <span className="chat-history-q">{p.q}</span>
                        <span className="chat-history-time">{p.endedAt ? formatEndedAt(p.endedAt) : '…'}</span>
                      </div>
                    ))
                  )}
                </div>
                {/* Full-text filter over the list; owns arrow/Enter/Escape while the dropdown is
                    open (unlike .skillmenu, whose equivalent keys live on the main textarea —
                    here the search box itself holds focus, so it holds the keys too). At the
                    bottom, right under the list, like a normal chat composer. */}
                <input
                  type="text"
                  className="chat-history-search"
                  value={historyFilter}
                  onChange={(e) => {
                    setHistoryFilter(e.target.value)
                    setHistoryMenuIdx(Number.MAX_SAFE_INTEGER) // re-narrowed list: default to its most recent match
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault()
                      setHistoryMenuIdx(Math.min(activeHistoryIdx + 1, displayHistory.length - 1))
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault()
                      setHistoryMenuIdx(Math.max(activeHistoryIdx - 1, 0))
                    } else if (e.key === 'Escape') {
                      e.preventDefault()
                      setHistoryMenuOpen(false)
                      inputRef.current?.focus()
                    } else if (e.key === 'Enter') {
                      e.preventDefault()
                      const picked = displayHistory[activeHistoryIdx]
                      if (picked) recallDraft(picked.q)
                      setHistoryMenuOpen(false)
                    }
                  }}
                  placeholder={t('chat_history_search_ph')}
                  aria-label={t('chat_history_search_ph')}
                  aria-controls="chat-history-listbox"
                  autoFocus
                />
              </div>
            )}
          </div>
        </div>
        {/* ── Retrieval-tool toggles: which knowledge bases the agent may search on the NEXT
            message. Independently toggleable (both, either, or neither on) — not a radio group. */}
        <div className="kbtoggle" role="group" aria-label={t('chat_kb_group')}>
          <div className="seg">
            <button type="button" className={bidKb ? 'on' : ''} aria-pressed={bidKb}
              title={t('chat_kb_bid_title')} onClick={() => setBidKb((v) => !v)}>{t('chat_kb_bid')}</button>
            <button type="button" className={corporateKb ? 'on' : ''} aria-pressed={corporateKb === true}
              title={t('chat_kb_corporate_title')} onClick={() => setCorporateKb((v) => !v)}>{t('chat_kb_corporate')}</button>
          </div>
        </div>
        <div className="ctxbar">
          <span className="ctxsize" title={`${ctx.tokens} tokens`}>
            🧠 {t('ctx_lbl')} {fmtTokens(ctx.tokens)} / {fmtTokens(MAX_CTX_TOKENS)}
            {ctx.trimmed && <span className="ctxfull"> · {t('ctx_full')}</span>}
          </span>
          <button
            className="ctxclear"
            onClick={clearChat}
            disabled={!hasHistory}
            title={t('ctx_clear_title')}
          >
            {t('ctx_clear')}
          </button>
        </div>

        {/* The proactive expiry warning, as a one-line strip INSIDE the composer — deliberately not
            a fifth child of .assistant, which is a four-row grid matching this component's four
            children exactly (styles.css). Dismissible for 24 h; Settings keeps showing it, because
            that is where the member goes to deal with it. */}
        {showExpiryStrip && (
          <div className="busybar expirybar" role="status" aria-live="polite">
            <span className="expirybar-text">
              {expiresInDays === 0
                ? t('claude_expiry_today')
                : expiresInDays === 1
                  ? t('claude_expiry_one_day')
                  : t('claude_expiry_days').replace('{n}', () => String(expiresInDays))}
            </span>
            <button type="button" className="mini ghost" onClick={() => navigate(aiAgentSettingsPath)}>
              {t('claude_expiry_renew')}
            </button>
            <button type="button" className="mini ghost" onClick={dismissExpiry}>
              {t('claude_expiry_dismiss')}
            </button>
          </div>
        )}

        {/* ── Queued follow-ups: one "Next: …" line each, run in order once the agent is free. ── */}
        {queue.length > 0 && (
          <ul className="chat-queue" aria-label={t('chat_queue_label')}>
            {queue.map((qp) => {
              const label = qp.text.trim() || t('chat_img_only')
              // One line on screen (a multi-line prompt collapses, then "…" at the panel width);
              // the tooltip keeps the full prompt.
              const line = label.replace(/\s+/g, ' ')
              return (
                <li className="chat-queue-item" key={qp.id}>
                  <span className="chat-queue-text" title={label}>
                    {t('chat_queue_next').replace('{q}', () => line)}
                  </span>
                  <IconHourglass className="chat-queue-wait" />
                  <button
                    type="button"
                    className="chat-queue-x"
                    aria-label={t('chat_queue_remove')}
                    title={t('chat_queue_remove')}
                    onClick={() => setQueue((q) => q.filter((x) => x.id !== qp.id))}
                  >
                    ×
                  </button>
                </li>
              )
            })}
          </ul>
        )}
        <span className="visually-hidden" role="status" aria-live="polite">
          {queueNote}
        </span>
        <span className="visually-hidden" role="status" aria-live="polite">
          {answerNote}
        </span>

        {/* ── Attached images: what will ride with the NEXT message (paste / drop / pick).
            They never reach the backend — the passerelle hands them straight to the local
            agent's SDK (lib/chat-images.ts). ── */}
        {(attachments.length > 0 || attachedDecks.length > 0 || stagingDecks.length > 0 || attachError) && (
          <div className="chat-attachments" role="group" aria-label={t('chat_img_group')}>
            {attachments.map((img, i) => (
              <span className="chat-attach" key={`${img.name}-${i}`}>
                {/* alt="" on purpose: the file name sits right beside it, so a label here would
                    just be read out twice. */}
                <img className="chat-attach-thumb" src={chatImageSrc(img)} alt="" />
                <span className="chat-attach-name" title={img.name}>{img.name}</span>
                <button
                  type="button"
                  className="chat-attach-x"
                  aria-label={t('chat_img_remove').replace('{name}', img.name)}
                  onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))}
                >
                  ×
                </button>
              </span>
            ))}
            {/* Decks are staged on the backend already: the message will carry their ids (AB#452). */}
            {attachedDecks.map((d, i) => (
              <span className="chat-attach" key={d.stagedId}>
                <span className="chat-attach-deck" aria-hidden="true"><FileIcon name={d.name} /></span>
                <span className="chat-attach-name" title={d.name}>{d.name}</span>
                <button
                  type="button"
                  className="chat-attach-x"
                  aria-label={t('chat_img_remove').replace('{name}', d.name)}
                  onClick={() => setAttachedDecks((prev) => prev.filter((_, j) => j !== i))}
                >
                  ×
                </button>
              </span>
            ))}
            {stagingDecks.map((d) => (
              <span className="chat-attach staging" key={d.key} role="status">
                <span className="chat-attach-deck" aria-hidden="true"><FileIcon name={d.name} /></span>
                <span className="chat-attach-name" title={d.name}>{t('chat_deck_staging').replace('{name}', d.name)}</span>
              </span>
            ))}
            {attachError && (
              <span className="chat-attach-err" role="status">
                {attachError}
              </span>
            )}
          </div>
        )}

        <div
          className={'inputbar' + (dropping ? ' dropping' : '')}
          onDragOver={(e) => {
            if (!e.dataTransfer.types.includes('Files')) return
            e.preventDefault()
            setDropping(true)
          }}
          // dragleave bubbles up from the children too, so "left the textarea for its own
          // container" must not read as "left the drop zone" — that flickers the hint on and off.
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropping(false)
          }}
          onDrop={(e) => {
            setDropping(false)
            const files = [...imageFilesFrom(e.dataTransfer.files), ...deckFilesFrom(e.dataTransfer.files)]
            if (files.length === 0) return
            e.preventDefault()
            void attachFiles(files)
          }}
        >
          {dropping && <div className="chat-drop-hint" aria-hidden="true">{t('chat_img_drop')}</div>}
          {skillMenuOpen && (
            <div className="skillmenu" role="listbox" aria-label={t('skill_menu_label')} ref={menuRef}>
              {menuNodes.length === 0 ? (
                <div className="skillmenu-empty">{t('skill_menu_no_skills')}</div>
              ) : (
                menuNodes.map((n, i) => (
                  <div
                    key={n.kind + ':' + n.nodeId}
                    className={'skillmenu-item' + (i === skillMenuIdx ? ' sel' : '')}
                    role="option"
                    aria-selected={i === skillMenuIdx}
                    onMouseEnter={() => setSkillMenuIdx(i)}
                    onMouseDown={(e) => {
                      e.preventDefault() // keep focus on textarea
                      completeSkill(n)
                    }}
                  >
                    <span className="skillmenu-section">{n.section}</span>
                    {'statut' in n && !n.navHidden && (
                      <span className={'skillmenu-statut s-' + n.statut}>{t(('wf_statut_' + n.statut) as Parameters<typeof t>[0])}</span>
                    )}
                    {'kind' in n && n.kind === 'export' && (
                      <span className="skillmenu-statut s-a_faire">⇩ Export</span>
                    )}
                    {'kind' in n && n.kind === 'action' && (
                      <span className="skillmenu-statut s-valide">⚡ Action</span>
                    )}
                    {'kind' in n && n.kind === 'user-skill' && (
                      <span className="skillmenu-statut s-user-skill">{t('skill_user_badge')}</span>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
          <textarea
            ref={inputRef}
            rows={1}
            value={draft}
            onChange={(e) => {
              const val = e.target.value
              setDraft(val)
              autosize()
              // Reset menu index when filter changes
              if (val.startsWith('/') && !val.includes(' ')) setSkillMenuIdx(0)
              // Typing by hand exits shell-style recall — the next ArrowUp starts fresh from
              // the most recent request rather than continuing a stale walk.
              if (recallIdx !== -1) setRecallIdx(-1)
            }}
            onPaste={(e) => {
              // Ctrl+V on a screenshot (or a copied deck): keep the file, let any accompanying text paste normally.
              const files = [...imageFilesFrom(e.clipboardData?.files), ...deckFilesFrom(e.clipboardData?.files)]
              if (files.length === 0) return
              e.preventDefault()
              void attachFiles(files)
            }}
            onKeyDown={(e) => {
              if (skillMenuOpen) {
                if (e.key === 'ArrowDown') {
                  e.preventDefault()
                  setSkillMenuIdx((i) => Math.min(i + 1, menuNodes.length - 1))
                  return
                }
                if (e.key === 'ArrowUp') {
                  e.preventDefault()
                  setSkillMenuIdx((i) => Math.max(i - 1, 0))
                  return
                }
                if (e.key === 'Escape') {
                  e.preventDefault()
                  // Escape: clear the slash command entirely
                  setDraft('')
                  autosize()
                  return
                }
                if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
                  e.preventDefault()
                  if (menuNodes[skillMenuIdx]) completeSkill(menuNodes[skillMenuIdx])
                  return
                }
              }
              // (The past-requests dropdown's own arrow/Enter/Escape keys live on its search
              // input, not here — that input holds focus while the dropdown is open, see the
              // .chat-history-search onKeyDown above.)
              // Shell-style recall: in an EMPTY composer (no menu open), ArrowUp/ArrowDown walk
              // back/forward through past requests, mirroring a terminal's command history.
              // `recallIdx !== -1` lets the walk continue once it has populated the draft (which
              // is no longer empty); typing by hand resets it via onChange above.
              if (
                !skillMenuOpen && !historyMenuOpen && (draft === '' || recallIdx !== -1) &&
                (e.key === 'ArrowUp' || e.key === 'ArrowDown') && promptHistory.length > 0
              ) {
                e.preventDefault()
                const next = e.key === 'ArrowUp'
                  ? Math.min(recallIdx + 1, promptHistory.length - 1)
                  : Math.max(recallIdx - 1, -1)
                setRecallIdx(next)
                recallDraft(next >= 0 ? promptHistory[next]!.q : '')
                return
              }
              // Enter sends; Shift+Enter or Ctrl/⌘+Enter insert a newline instead (left to the
              // textarea's own default behaviour — nothing to do here but decline to send).
              // isComposing guards an IME's confirming Enter (e.g. typing Japanese/Chinese) from
              // firing a send before the candidate is even committed to the field.
              if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.nativeEvent.isComposing) {
                e.preventDefault()
                void send()
              }
            }}
            placeholder={
              busy && queueFull
                ? t('chat_queue_full')
                : busy
                ? t('ph_busy')
                : activeSkillNode && draft.includes(' ')
                  ? t('ph_skill')
                  : t('ph')
            }
            aria-label="Chat with Niobe"
            aria-expanded={skillMenuOpen}
            aria-autocomplete="list"
          />
          {/* The keyboard/mouse twin of paste-and-drop. The input stays in the DOM (visually
              hidden rather than `hidden`) so the button's click reaches a focusable control. */}
          <input
            ref={fileInputRef}
            className="visually-hidden"
            type="file"
            accept={[...ACCEPTED_IMAGE_TYPES, DECK_ACCEPT].join(',')}
            multiple
            tabIndex={-1}
            aria-hidden="true"
            onChange={(e) => {
              const files = [...imageFilesFrom(e.target.files), ...deckFilesFrom(e.target.files)]
              e.target.value = '' // re-picking the same file must fire onChange again
              void attachFiles(files)
            }}
          />
          <button
            type="button"
            className="attachbtn"
            aria-label={t('chat_img_attach')}
            title={t('chat_img_attach')}
            onClick={() => fileInputRef.current?.click()}
          >
            <IconAttach />
          </button>
          <button
            className={'iconbtn' + (anyRunning ? ' stop' : '')}
            aria-label={anyRunning ? t('stop_generating') : 'Send'}
            title={anyRunning ? t('stop_generating') : 'Send (Enter) · Shift+Enter for a new line'}
            onClick={() => (anyRunning ? stopRunning() : void send())}
            disabled={!anyRunning && signInBlocking}
          >
            {anyRunning ? <span className="stopicon" aria-hidden="true" /> : '↑'}
          </button>
        </div>
      </div>

    </aside>
  )
}
